/**
 * Dashboard page component - Shows real-time data from the database
 */

import { useQuery } from '@tanstack/react-query'
import { Link } from 'react-router-dom'
import { useAuth } from '@/core/auth/AuthContext'
import { leaveApi } from '@/modules/leave/api/leaveApi'
import { learningApi } from '@/modules/learning/api/learningApi'

const DashboardPage = () => {
  const { user } = useAuth()
  const currentYear = new Date().getFullYear()

  // Check if user is manager, HR, or admin (can see pending approvals)
  const canApprove = user?.roles?.some(role =>
    role.name === 'admin' || role.name === 'hr' || role.name === 'manager'
  ) ?? false

  // Fetch leave balances
  const { data: balancesData, isLoading: isLoadingBalances } = useQuery({
    queryKey: ['leaveBalances', currentYear],
    queryFn: () => leaveApi.getLeaveBalances(currentYear),
  })

  // Fetch pending leave requests (for managers/HR/admin)
  const { data: pendingRequestsData, isLoading: isLoadingPending } = useQuery({
    queryKey: ['pendingLeaveRequests'],
    queryFn: () => leaveApi.getLeaveRequests({ status: 'pending', limit: 100 }),
    enabled: canApprove,
  })

  // Fetch my training records
  const { data: trainingData, isLoading: isLoadingTraining } = useQuery({
    queryKey: ['myTrainingRecords', currentYear],
    queryFn: () => learningApi.getMyRecords({ page: 1, limit: 100, year: currentYear }),
  })

  // Calculate total available leave days
  const balances = balancesData?.data || []
  const totalAvailableDays = balances.reduce((sum, balance) => sum + (balance.available || 0), 0)
  const annualLeaveBalance = balances.find(b => b.leave_type?.name?.toLowerCase().includes('annual'))

  // Count pending approvals
  const pendingRequests = pendingRequestsData?.data || []
  const pendingCount = pendingRequests.length

  // Count training records
  const trainingRecords = trainingData?.data || []
  const verifiedCount = trainingRecords.filter(r => r.verification_status === 'verified').length
  const pendingVerificationCount = trainingRecords.filter(r => r.verification_status === 'pending').length

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Dashboard</h2>
        <p className="text-gray-600 mt-1">Welcome back, {user?.full_name || user?.display_name}!</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        {/* Annual Leave Balance */}
        <Link
          to="/leave/balance"
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-red-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="text-xs font-medium text-gray-400 uppercase">Leave</span>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-gray-900">
              {isLoadingBalances ? '...' : annualLeaveBalance?.available ?? totalAvailableDays}
            </p>
            <span className="text-sm text-gray-500">days</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            {annualLeaveBalance ? 'Annual Leave Available' : 'Total Leave Available'}
          </p>
        </Link>

        {/* Pending Approvals (for managers/HR/admin) */}
        {canApprove && (
          <Link
            to="/leave/approvals"
            className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
                <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              </div>
              <span className="text-xs font-medium text-gray-400 uppercase">Approvals</span>
            </div>
            <div className="flex items-baseline gap-2">
              <p className={`text-3xl font-bold ${pendingCount > 0 ? 'text-orange-600' : 'text-gray-900'}`}>
                {isLoadingPending ? '...' : pendingCount}
              </p>
              <span className="text-sm text-gray-500">pending</span>
            </div>
            <p className="text-sm text-gray-500 mt-1">Leave Requests to Review</p>
          </Link>
        )}

        {/* Training Records */}
        <Link
          to="/learning/my-records"
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
            </div>
            <span className="text-xs font-medium text-gray-400 uppercase">Learning</span>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-gray-900">
              {isLoadingTraining ? '...' : verifiedCount}
            </p>
            <span className="text-sm text-gray-500">verified</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Training Records in {currentYear}
            {pendingVerificationCount > 0 && (
              <span className="text-orange-500 ml-1">({pendingVerificationCount} pending)</span>
            )}
          </p>
        </Link>

        {/* My Leave Requests */}
        <Link
          to="/leave/requests"
          className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 hover:shadow-md transition-shadow"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="w-12 h-12 bg-rose-100 rounded-xl flex items-center justify-center">
              <svg className="w-6 h-6 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
            </div>
            <span className="text-xs font-medium text-gray-400 uppercase">Requests</span>
          </div>
          <div className="flex items-baseline gap-2">
            <p className="text-3xl font-bold text-gray-900">
              {isLoadingBalances ? '...' : balances.reduce((sum, b) => sum + (b.used || 0), 0)}
            </p>
            <span className="text-sm text-gray-500">days used</span>
          </div>
          <p className="text-sm text-gray-500 mt-1">
            Leave Taken in {currentYear}
          </p>
        </Link>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 mb-8">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link
            to="/leave/new"
            className="flex flex-col items-center p-4 bg-red-50 rounded-xl hover:bg-red-100 transition-colors"
          >
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mb-2">
              <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
            </div>
            <span className="text-sm font-medium text-gray-700">Request Leave</span>
          </Link>

          <Link
            to="/learning/add-record"
            className="flex flex-col items-center p-4 bg-emerald-50 rounded-xl hover:bg-emerald-100 transition-colors"
          >
            <div className="w-10 h-10 bg-emerald-100 rounded-full flex items-center justify-center mb-2">
              <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="text-sm font-medium text-gray-700">Add Training</span>
          </Link>

          <Link
            to="/leave/calendar"
            className="flex flex-col items-center p-4 bg-rose-50 rounded-xl hover:bg-rose-100 transition-colors"
          >
            <div className="w-10 h-10 bg-rose-100 rounded-full flex items-center justify-center mb-2">
              <svg className="w-5 h-5 text-rose-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
            </div>
            <span className="text-sm font-medium text-gray-700">Leave Calendar</span>
          </Link>

          <Link
            to="/learning/trainings"
            className="flex flex-col items-center p-4 bg-amber-50 rounded-xl hover:bg-amber-100 transition-colors"
          >
            <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center mb-2">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
              </svg>
            </div>
            <span className="text-sm font-medium text-gray-700">Training Catalog</span>
          </Link>
        </div>
      </div>

      {/* Leave Balance Breakdown */}
      {balances.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Leave Balance Summary</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {balances.map((balance) => (
              <div
                key={balance.id}
                className="flex items-center gap-4 p-4 bg-gray-50 rounded-xl"
              >
                <div
                  className="w-3 h-12 rounded-full"
                  style={{ backgroundColor: balance.leave_type?.color || '#6b7280' }}
                />
                <div className="flex-1">
                  <p className="font-medium text-gray-900">{balance.leave_type?.name}</p>
                  <div className="flex items-center gap-4 text-sm text-gray-500">
                    <span>{balance.available} available</span>
                    <span className="text-gray-300">|</span>
                    <span>{balance.used} used</span>
                    {balance.pending > 0 && (
                      <>
                        <span className="text-gray-300">|</span>
                        <span className="text-orange-500">{balance.pending} pending</span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  )
}

export default DashboardPage
